/**
 * Multi-step wizard for custom request configuration.
 * Steps: Party Size -> Times -> Table Types (optional) -> Min Spend -> Expiration -> Payment.
 * Refetches settings with context (date, party, times) for accurate price/expiration options.
 * @module Widget/CustomRequest/CustomRequestStepsPage
 */

import * as Sentry from '@sentry/react';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue, useAtom } from 'jotai';
import {
  useWidgetState,
  useRestaurantId,
  AuthAtom,
  RestaurantAtom,
} from '../lib/state.ts';
import { getRequestOptions, getCustomRequestSettings, getRestaurant } from '@/lib/api.ts';
import type {
  RequestOptionsTime,
  CustomRequestPriceOption,
  CustomRequestExpirationOption,
  CustomRequestTableOption,
} from '../lib/types.ts';
import type { RestaurantResponseData } from '@/lib/types.ts';
import PartySizeStep from './steps/PartySizeStep.tsx';
import TimeSelectionStep from './steps/TimeSelectionStep.tsx';
import TableTypeStep from './steps/TableTypeStep.tsx';
import MinSpendStep from './steps/MinSpendStep.tsx';
import ExpirationTimeStep from './steps/ExpirationTimeStep.tsx';
import { FORM_CONSTRAINTS } from './constants.ts';
import styles from './CustomRequest.module.css';

type StepId = 'party' | 'times' | 'tableTypes' | 'minSpend' | 'expiration';

/**
 * Orchestrates the multi-step custom request form.
 * Fetches request options and settings from the API, manages step navigation,
 * and persists selections to widget state before proceeding to payment.
 *
 * @returns Rendered multi-step wizard
 */
const CustomRequestStepsPage: React.FC = () => {
  const navigate = useNavigate();
  const restaurantId = useRestaurantId();
  const [storedRestaurant, setStoredRestaurant] = useAtom(RestaurantAtom);
  const auth = useAtomValue(AuthAtom);
  const [widget, updateWidget] = useWidgetState();

  const [localRestaurant, setLocalRestaurant] = useState<RestaurantResponseData | null>(storedRestaurant);

  /** Fetch restaurant data when the atom is empty (e.g. after page refresh). */
  useEffect(() => {
    if (storedRestaurant) {
      setLocalRestaurant(storedRestaurant);
      return;
    }
    if (!restaurantId) return;
    const date = widget.custom_request?.date ?? new Date().toISOString().slice(0, 10);
    void getRestaurant(restaurantId, date, '0:00', 'steps', auth?.token ?? null).then((res) => {
      if (res?.data) {
        setLocalRestaurant(res.data);
        setStoredRestaurant(res.data);
      }
    });
  }, [restaurantId, storedRestaurant, auth?.token]);

  const restaurant = localRestaurant;

  useEffect(() => {
    if (!auth?.token) {
      navigate('../request', { replace: true });
    }
  }, [auth?.token, navigate]);

  /** Redirect to date picker when the stored custom request belongs to a different restaurant. */
  useEffect(() => {
    if (
      restaurantId &&
      widget.custom_request_restaurant_id !== undefined &&
      widget.custom_request_restaurant_id !== restaurantId
    ) {
      updateWidget({
        custom_request: undefined,
        custom_request_step: undefined,
        custom_request_restaurant_id: undefined,
      });
      navigate('../request', { replace: true });
    }
  }, [restaurantId, widget.custom_request_restaurant_id, navigate, updateWidget]);

  const cr = widget.custom_request;

  const [partySize, setPartySize] = useState(cr?.party_size ?? restaurant?.min_size ?? 2);
  const [selectedTimes, setSelectedTimes] = useState<string[]>(cr?.selected_times ?? []);
  const [selectedTableTypes, setSelectedTableTypes] = useState<number[]>(cr?.selected_table_types ?? []);
  const [perPersonSpend, setPerPersonSpend] = useState(cr?.per_person_spend ?? 0);
  const [expirationHours, setExpirationHours] = useState<number | undefined>(cr?.expiration_hours);

  const [availableTimes, setAvailableTimes] = useState<RequestOptionsTime[]>([]);
  const [timesLoading, setTimesLoading] = useState(false);
  const [priceOptions, setPriceOptions] = useState<CustomRequestPriceOption[]>([]);
  const [expirationOptions, setExpirationOptions] = useState<CustomRequestExpirationOption[]>([]);
  const [tableOptions, setTableOptions] = useState<CustomRequestTableOption[]>([]);
  const [responseTime, setResponseTime] = useState<string | null>(null);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [settingsRefetching, setSettingsRefetching] = useState(false);
  const [restaurantLoading, setRestaurantLoading] = useState(!storedRestaurant);

  const minSize = restaurant?.min_size ?? FORM_CONSTRAINTS.MIN_GUESTS;
  const maxSize = restaurant?.max_size ?? FORM_CONSTRAINTS.MAX_GUESTS;

  /** Update partySize bounds when restaurant data arrives after initial render. */
  useEffect(() => {
    if (!restaurant) return;
    setRestaurantLoading(false);
    setPartySize((prev) => {
      const min = restaurant.min_size ?? FORM_CONSTRAINTS.MIN_GUESTS;
      const max = restaurant.max_size ?? FORM_CONSTRAINTS.MAX_GUESTS;
      if (prev < min) return min;
      if (prev > max) return max;
      return prev;
    });
  }, [restaurant]);

  const hasTableTypes = tableOptions.length > 0;
  const hasExpirationOptions = expirationOptions.filter(
    (o) => o.hours >= FORM_CONSTRAINTS.MIN_EXPIRATION_HOURS
  ).length > 0;

  const steps = useMemo<StepId[]>(() => {
    const s: StepId[] = ['party', 'times'];
    if (hasTableTypes) s.push('tableTypes');
    s.push('minSpend');
    if (hasExpirationOptions) s.push('expiration');
    return s;
  }, [hasTableTypes, hasExpirationOptions]);

  const [currentStepIndex, setCurrentStepIndex] = useState(widget.custom_request_step ?? 0);
  const currentStep = steps[currentStepIndex] ?? 'party';

  /** Persist step index to sessionStorage so it survives refresh. */
  useEffect(() => {
    if (widget.custom_request_step !== currentStepIndex) {
      updateWidget({ custom_request_step: currentStepIndex });
    }
  }, [currentStepIndex]);

  /** Clamp restored step index once dynamic steps are computed. */
  useEffect(() => {
    if (currentStepIndex >= steps.length && steps.length > 0) {
      setCurrentStepIndex(steps.length - 1);
    }
  }, [steps.length]);

  const settingsLoadedOnce = useRef(false);

  /**
   * Loads settings with optional context params.
   * On first load (no times yet), fetches table_options + response_time.
   * After times are selected, refetches with date/party/times for price_options + expiration_options.
   */
  const loadSettings = useCallback(async (opts?: {
    date?: string;
    partySize?: number;
    startTime?: string;
    endTime?: string;
  }) => {
    if (!restaurantId || !auth?.token) return;
    try {
      const response = await getCustomRequestSettings(restaurantId, auth.token, opts);
      const settings = response?.data?.customRequestSettings;
      if (!settings) return;

      setTableOptions(settings.table_options ?? []);
      setResponseTime(settings.response_time?.average_response_time_verbiage ?? null);

      if (settings.price_options?.length) {
        setPriceOptions(settings.price_options);
        const hasRestoredSpend = (cr?.per_person_spend ?? 0) > 0;
        if (!hasRestoredSpend && (perPersonSpend === 0 || !settingsLoadedOnce.current)) {
          const defaultOpt = settings.price_options.find((o) => o.default);
          setPerPersonSpend(defaultOpt?.price ?? settings.price_options[0].price);
        }
      }

      if (settings.expiration_options?.length) {
        setExpirationOptions(settings.expiration_options);
        const hasRestoredExpiration = cr?.expiration_hours !== undefined;
        if (!hasRestoredExpiration && (expirationHours === undefined || !settingsLoadedOnce.current)) {
          const valid = settings.expiration_options.filter(
            (o) => o.hours >= FORM_CONSTRAINTS.MIN_EXPIRATION_HOURS
          );
          if (valid[0]) setExpirationHours(valid[0].hours);
        }
      }

      settingsLoadedOnce.current = true;
      setSettingsLoaded(true);
    } catch (err) {
      Sentry.captureException(err, { extra: { restaurantId } });
      setSettingsLoaded(true);
    }
  }, [restaurantId, auth?.token, perPersonSpend, expirationHours]);

  /**
   * On mount, fetch settings with full context if the user had already progressed
   * past the times step (i.e. they had selected times before refresh).
   * This ensures restaurant-specific price_options / expiration_options are loaded.
   */
  useEffect(() => {
    if (!restaurantId || !auth?.token) return;
    const restoredTimes = cr?.selected_times ?? [];
    if (restoredTimes.length > 0 && cr?.date) {
      const sorted = [...restoredTimes].sort();
      void loadSettings({
        date: cr.date,
        partySize: cr?.party_size ?? partySize,
        startTime: sorted[0],
        endTime: sorted[sorted.length - 1],
      });
    } else {
      void loadSettings();
    }
  }, [restaurantId, auth?.token]);

  useEffect(() => {
    if (!restaurantId || !auth?.token || !cr?.date) return;
    setTimesLoading(true);
    getRequestOptions(auth.token, restaurantId, cr.date, partySize)
      .then((response) => {
        setAvailableTimes(response?.times ?? []);
        setSelectedTimes((prev) => {
          const validValues = new Set((response?.times ?? []).map((t) => {
            const match = t.local.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
            if (!match) return t.local;
            let hours = parseInt(match[1], 10);
            const minutes = match[2];
            const period = match[3].toUpperCase();
            if (period === 'PM' && hours !== 12) hours += 12;
            if (period === 'AM' && hours === 12) hours = 0;
            return `${hours.toString().padStart(2, '0')}:${minutes}`;
          }));
          return prev.filter((t) => validValues.has(t));
        });
      })
      .catch(() => setAvailableTimes([]))
      .finally(() => setTimesLoading(false));
  }, [restaurantId, auth?.token, cr?.date, partySize]);

  /**
   * Refetch settings with full context when advancing past the times step,
   * so the resolver returns restaurant-specific price_options and expiration_options.
   * Returns a promise that resolves when settings are loaded.
   */
  const refetchSettingsWithTimes = useCallback(async () => {
    if (!cr?.date || selectedTimes.length === 0) return;
    const sorted = [...selectedTimes].sort();
    setSettingsRefetching(true);
    await loadSettings({
      date: cr.date,
      partySize,
      startTime: sorted[0],
      endTime: sorted[sorted.length - 1],
    });
    setSettingsRefetching(false);
  }, [cr?.date, selectedTimes, partySize, loadSettings]);

  /** Persist current selections to widget state so they survive back-navigation and refresh. */
  const persistSelections = useCallback(() => {
    const tableTypeNames = selectedTableTypes.map(
      (id) => tableOptions.find((t) => Number(t.id) === Number(id))?.name ?? `Type ${id}`
    );
    const expDesc = expirationHours !== undefined
      ? expirationOptions.find((o) => o.hours === expirationHours)?.description ?? `${expirationHours}h`
      : undefined;

    updateWidget({
      custom_request: {
        date: cr?.date ?? '',
        party_size: partySize,
        selected_times: selectedTimes,
        selected_table_types: selectedTableTypes,
        selected_table_type_names: tableTypeNames,
        per_person_spend: perPersonSpend,
        minimum_spend: perPersonSpend * partySize,
        expiration_hours: expirationHours,
        expiration_description: expDesc,
        comments: cr?.comments,
      },
      custom_request_restaurant_id: restaurantId ?? undefined,
    });
  }, [cr?.date, cr?.comments, partySize, selectedTimes, selectedTableTypes, perPersonSpend, expirationHours, tableOptions, expirationOptions, updateWidget, restaurantId]);

  const canContinue = useCallback((): boolean => {
    switch (currentStep) {
      case 'party':
        return partySize >= minSize && partySize <= maxSize;
      case 'times':
        return selectedTimes.length > 0;
      case 'tableTypes':
        return selectedTableTypes.length > 0;
      case 'minSpend':
        return perPersonSpend > 0;
      case 'expiration':
        return expirationHours !== undefined;
      default:
        return false;
    }
  }, [currentStep, partySize, minSize, maxSize, selectedTimes, selectedTableTypes, perPersonSpend, expirationHours]);

  const handleNext = useCallback(async () => {
    if (currentStep === 'times') {
      await refetchSettingsWithTimes();
    }

    persistSelections();

    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(currentStepIndex + 1);
    } else {
      updateWidget({ custom_request_step: undefined });
      navigate('../payment');
    }
  }, [currentStep, currentStepIndex, steps, persistSelections, updateWidget, navigate, refetchSettingsWithTimes]);

  const handleBack = useCallback(() => {
    persistSelections();

    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    } else {
      updateWidget({ custom_request_step: undefined });
      navigate('../request');
    }
  }, [currentStepIndex, persistSelections, navigate, updateWidget]);

  const stepLabels: Record<StepId, string> = {
    party: 'Party Size',
    times: 'Start Times',
    tableTypes: 'Table Type',
    minSpend: 'Minimum Spend',
    expiration: 'Expiration',
  };

  if (restaurantLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className="flex flex-col items-center gap-4 py-16">
            <div className={styles.spinner} />
            <p className={styles.sectionSubtitle}>Loading...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/* Progress dots */}
        <div className={styles.progressDots}>
          {steps.map((_, i) => (
            <div
              key={i}
              className={
                i === currentStepIndex
                  ? styles.progressDotActive
                  : i < currentStepIndex
                    ? styles.progressDotCompleted
                    : styles.progressDot
              }
            />
          ))}
        </div>

        {/* Back button */}
        <button className={styles.backButton} onClick={handleBack}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        {/* Step title */}
        <h2 className={styles.sectionTitle}>{stepLabels[currentStep]}</h2>

        {/* Step content */}
        <div className={styles.stepContent}>
          {currentStep === 'party' && (
            <PartySizeStep
              value={partySize}
              onChange={setPartySize}
              minSize={minSize}
              maxSize={maxSize}
              responseTimeText={responseTime}
            />
          )}

          {currentStep === 'times' && (
            <TimeSelectionStep
              times={availableTimes}
              selectedTimes={selectedTimes}
              onChange={setSelectedTimes}
              loading={timesLoading}
            />
          )}

          {currentStep === 'tableTypes' && (
            <TableTypeStep
              tableTypes={tableOptions}
              selectedTypes={selectedTableTypes}
              onChange={setSelectedTableTypes}
            />
          )}

          {currentStep === 'minSpend' && settingsRefetching && (
            <div className="flex flex-col items-center gap-4 py-12">
              <div className={styles.spinner} />
              <p className={styles.sectionSubtitle}>Loading pricing options...</p>
            </div>
          )}

          {currentStep === 'minSpend' && settingsLoaded && !settingsRefetching && (
            <MinSpendStep
              priceOptions={priceOptions}
              value={perPersonSpend}
              onChange={setPerPersonSpend}
              partySize={partySize}
              currencySymbol={restaurant?.currency_symbol ?? '$'}
            />
          )}

          {currentStep === 'expiration' && (
            <ExpirationTimeStep
              options={expirationOptions}
              selectedHours={expirationHours}
              onChange={setExpirationHours}
              responseTimeText={responseTime}
              restaurantName={restaurant?.name}
            />
          )}
        </div>

      </div>

      {/* CTA button — sticky footer */}
      <div className={styles.stickyFooter}>
        <button
          className={styles.ctaButton}
          onClick={handleNext}
          disabled={!canContinue() || settingsRefetching}
        >
          Continue
        </button>
      </div>
    </div>
  );
};

export default CustomRequestStepsPage;
